import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Land at the top of every page you navigate TO.
 *
 * A router swap only replaces the DOM — the window keeps whatever scroll offset
 * it had, so clicking a venue near the bottom of the browse grid dropped you
 * into the middle of that venue's page.
 *
 * Back/forward (POP) is deliberately left alone: returning to the results should
 * put you back where you were in the list, not at the top of it.
 *
 * Only a change of page counts. A query-string update on the same page (the
 * venues search, filters, sort) is a PUSH too, and reacting to the navigation
 * type alone threw the first search on /venues back to the top, cancelling its
 * scroll down to the results.
 */
export function ScrollToTop() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  const last = useRef(pathname);

  useEffect(() => {
    if (last.current === pathname) return;
    last.current = pathname;
    if (navigationType === 'POP') return;
    // Instant, not smooth — a new page shouldn't animate a scroll. It has to be
    // said: html carries `scroll-behavior: smooth`, which a bare scrollTo(0, 0)
    // obeys, and a smooth scroll started while the old page (or the open menu)
    // is swapped out was cut short by the layout change — "Book a venue" from a
    // scrolled page landed on the new page's footer and looked like it had not
    // responded at all.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname, navigationType]);

  return null;
}
